import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import { BASE_FIELD_BACKGROUND_COLOR, BASIC_BORDER_COLOR } from '../../../../themes/tokens';
import {
  ResponsiveContainer,
  BarChart,
  XAxis,
  YAxis,
  Bar,
  Cell,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { useResponsive } from '../../../../hooks/useResponsive';
import { CustomTooltip } from '../CustomTooltip/CustomTooltip';
import { MULTIPLE_SALON_CHART_KPI } from './constants';
import { useState } from 'react';
import { useSalonsByKpiData } from './useSalonsByKpiData';
import { getBarColor, getMultipleSalonsChartData } from './helpers';
import { DateRangeState } from '../../DateRangeControl/useDateRange';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { ChartData } from './types';

type Props = {
  title: string;
  dateRange: DateRangeState;
  salonClassification: string;
};

const SALONS_PER_PAGE = 10;

export const MultipleSalonsChart = ({ title, dateRange, salonClassification }: Props) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();

  const [pageNumber, setPageNumber] = useState(0);
  const [selectedKpi, setSelectedKpi] = useState<keyof typeof MULTIPLE_SALON_CHART_KPI>(
    (Object.keys(MULTIPLE_SALON_CHART_KPI) as Array<keyof typeof MULTIPLE_SALON_CHART_KPI>)[0],
  );

  const { salonsByKpiData } = useSalonsByKpiData({
    dateRange: dateRange,
    salonClassification: salonClassification,
    multipleSalonsChartKpi: selectedKpi,
    offset: pageNumber * SALONS_PER_PAGE,
    limit: SALONS_PER_PAGE,
  });

  const multipleSalonsChartData = getMultipleSalonsChartData(salonsByKpiData[selectedKpi]);

  const chartData: ChartData[] = multipleSalonsChartData.map((item) => {
    return { ...item, name: item.field };
  });

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        minHeight: {
          xs: '550px',
          sm: '550px',
          md: '100%',
        },
        height: '100%',
        p: 2,
        borderRadius: '8px',
        bgcolor: '#f8f7f6',
        border: `1px solid ${BASIC_BORDER_COLOR}`,
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        padding: '16px 8px',
      }}
    >
      <Box
        display="flex"
        flexDirection={isMobile ? 'column' : 'row'}
        justifyContent="space-between"
        gap={2}
      >
        <FormControl>
          <InputLabel>{t('dashboard.kpiCopy')}</InputLabel>
          <Select
            label={t('dashboard.kpiCopy')}
            value={selectedKpi}
            onChange={(e) =>
              setSelectedKpi(e.target.value as keyof typeof MULTIPLE_SALON_CHART_KPI)
            }
            sx={{
              width: '30ch',
              backgroundColor: BASE_FIELD_BACKGROUND_COLOR,
              '& .MuiSelect-select:focus': {
                backgroundColor: 'transparent',
              },
              fontSize: '10px',
            }}
          >
            {Object.keys(MULTIPLE_SALON_CHART_KPI).map((key) => {
              // Hide RPCT kpi when 'all' salon type selected
              if (salonClassification === '0' && key === 'rpct') return null;

              return (
                <MenuItem key={key} value={key}>
                  {t(`dashboard.kpi.${key}`)}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center' }}>
          {title}
        </Typography>
      </Box>
      <ResponsiveContainer width="100%">
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ left: 5, right: 10 }}
          barCategoryGap={2}
          barGap={2}
        >
          <XAxis
            type="number"
            domain={[0, 100]}
            ticks={[0, 20, 40, 60, 80, 100]}
            axisLine={false}
            tickLine={false}
            hide={true}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={isMobile ? 100 : 220}
            tick={{ fontSize: 11 }}
            tickFormatter={(value) => (isMobile ? value : value.replace(/ /g, '\u00A0'))}
            axisLine={false}
            tickLine={false}
          />
          <CartesianGrid horizontal={false} vertical strokeDasharray="1" />
          <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} content={<CustomTooltip />} />

          <Bar dataKey="value" barSize={7}>
            {chartData.map((entry, index) => (
              <Cell key={index} fill={getBarColor(entry)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <Box display={'flex'} justifyContent={'flex-end'}>
        <IconButton
          aria-label="Back"
          onClick={() => {
            setPageNumber((prev) => {
              if (prev > 0) return --prev;
              else return prev;
            });
          }}
          disabled={!chartData?.length || pageNumber + 1 === 1}
        >
          <ArrowBack />
        </IconButton>
        {salonsByKpiData[selectedKpi]?.totalCount && (
          <Typography sx={{ display: 'flex', alignItems: 'center' }}>
            {`Page ${pageNumber + 1} of ${Math.ceil(salonsByKpiData[selectedKpi].totalCount / SALONS_PER_PAGE)}`}
          </Typography>
        )}
        <IconButton
          aria-label="Forward"
          onClick={() => {
            setPageNumber((prev) => ++prev);
          }}
          disabled={
            !chartData?.length ||
            pageNumber + 1 === Math.ceil(salonsByKpiData[selectedKpi].totalCount / SALONS_PER_PAGE)
          }
        >
          <ArrowForward />
        </IconButton>
      </Box>
    </Paper>
  );
};
